import { Popover, PopoverPanel } from '@headlessui/react';
import classNames from 'classnames';
import { useField } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '~/App';
import { Option } from '~/types';
import { FormData } from '.';
import { Combolist } from '../../Combolist';
import { SearchInput } from '../../FindAddContact/SearchInput';
import { PopoverStateChangedEffect } from '../../PopoverStateChangedEffect';
import { Label } from '../../ui/Label';
import { MenuTransition } from '../../ui/Transition/MenuTransition';
import { CreateContactTypeDialog } from './CreateContactTypeDialog';

type CombolistItem = {
  id: string;
  name: string;
  $group?: string;
};

type Props = {
  options: Option[];
  error?: string;
};

const DISPLAY_LIMIT = 3;

const PopoverOpenObserver = ({
  open,
  onChange,
}: {
  open: boolean;
  onChange: (open: boolean) => void;
}) => {
  useEffect(() => {
    onChange(open);
  }, [open, onChange]);
  return null;
};

export function ContactTypesField({ options, error }: Props) {
  const [field, , helpers] = useField<FormData['types']>('types');
  const { setValue } = helpers;
  const selectedValues = useMemo(
    () => (Array.isArray(field.value) ? (field.value as string[]) : []),
    [field.value]
  );

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const { ability } = useAppContext();
  const canManageAccount = Boolean(ability?.can?.('manage', 'Account'));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingCreateValue, setPendingCreateValue] = useState<string>('');

  const combolistItems: CombolistItem[] = useMemo(
    () =>
      options.map((option) => ({
        id: option.value,
        name: option.label,
        $group: option.$group,
      })),
    [options]
  );

  const filteredItems: CombolistItem[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return combolistItems;
    return combolistItems.filter((it) => it.name.toLowerCase().includes(q));
  }, [combolistItems, query]);

  const optionsMap = useMemo(
    () => new Map(options.map((opt) => [opt.value, opt])),
    [options]
  );

  // Normalized query used for existence check and display in the create button
  const normalizedQuery = useMemo(() => query.trim().toLowerCase(), [query]);
  const labelExists = useMemo(
    () =>
      normalizedQuery
        ? options.some((o) => o.label.trim().toLowerCase() === normalizedQuery)
        : false,
    [options, normalizedQuery]
  );

  const selectedItems: CombolistItem[] = useMemo(
    () =>
      selectedValues.map((selectedValue) => {
        const option = optionsMap.get(selectedValue);
        return {
          id: selectedValue,
          name: option?.label || selectedValue,
          $group: option?.$group,
        };
      }),
    [selectedValues, optionsMap]
  );

  const handleCombolistChange = useCallback(
    (selectedIdentifiables: CombolistItem[]) => {
      const selectedValues = selectedIdentifiables.map((item) => item.id);
      setValue(selectedValues);
    },
    [setValue]
  );

  const handleCreate = useCallback(
    (newLabel: string) => {
      const label = String(newLabel || '').trim();
      if (!label) return;
      if (!canManageAccount) return;
      setPendingCreateValue(label);
      setIsDialogOpen(true);
    },
    [canManageAccount]
  );

  useEffect(() => {
    if (isPopoverOpen && containerRef.current) {
      const timeoutId = window.setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }, 100);
      return () => window.clearTimeout(timeoutId);
    }
  }, [isPopoverOpen]);

  const handleRemove = (id: string) => {
    const next = selectedValues.filter((v) => v !== id);
    setValue(next);
  };

  const handleClosed = () => {
    if (cancelling) {
      setCancelling(false);
      return;
    }
  };

  return (
    <div className='mb-4'>
      <Label>Type</Label>
      <Popover className='relative' ref={containerRef}>
        <Popover.Button
          as='div'
          className={classNames(
            'input relative mt-0.5 w-full overflow-hidden rounded border border-grey-20 bg-white pl-2 pr-3 focus-visible:border-grey-40 focus-visible:outline-none',
            selectedItems.length === 0 ? 'h-[46px]' : 'py-1'
          )}
        >
          <div className='flex items-center'>
            <div className='flex flex-1 flex-wrap gap-1'>
              {selectedValues.slice(0, DISPLAY_LIMIT).map((selectedValue) => {
                const option = optionsMap.get(selectedValue);
                return (
                  <span
                    key={selectedValue}
                    className='flex max-w-full items-center gap-2 rounded-md bg-hoverBg px-2.5 py-1.5'
                  >
                    {option?.label || selectedValue}
                    <button
                      type='button'
                      className='text-brand-200 hover:bg-brand-200 hover:text-brand-900 focus:bg-brand-500 focus:ring-brand-500 focus:ring-offset-brand-50 ml-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full focus:text-white focus:outline-none focus:ring-2 focus:ring-offset-2'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(selectedValue);
                      }}
                    >
                      <span className='sr-only'>
                        Remove {option?.label || selectedValue}
                      </span>
                      <svg
                        className='h-2 w-2'
                        stroke='currentColor'
                        fill='none'
                        viewBox='0 0 8 8'
                      >
                        <path
                          strokeLinecap='round'
                          strokeWidth='1.5'
                          d='m1 1 6 6m0-6L1 7'
                        />
                      </svg>
                    </button>
                  </span>
                );
              })}
              {selectedValues.length > DISPLAY_LIMIT && (
                <span className='flex max-w-full items-center gap-2 rounded-md bg-grey-5 px-2.5 py-1.5'>
                  <span className='truncate text-grey-50'>
                    +{selectedValues.length - DISPLAY_LIMIT} more
                  </span>
                </span>
              )}
            </div>
            <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
              <svg
                className='h-5 w-5 text-gray-400'
                viewBox='0 0 20 20'
                fill='currentColor'
                aria-hidden='true'
              >
                <path
                  fillRule='evenodd'
                  d='M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
          </div>
        </Popover.Button>
        {error && <p className='ErrorMessage'>{error}</p>}
        <MenuTransition>
          <PopoverPanel className='absolute top-full z-50 mt-0.5 w-full rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
            {({ open }) => (
              <>
                <PopoverOpenObserver open={open} onChange={setIsPopoverOpen} />
                <PopoverStateChangedEffect open={open} onClose={handleClosed} />

                <div className='sticky -top-1 z-50 -mt-1 mb-1 bg-white p-1 pb-0'>
                  <SearchInput
                    className='rounded-full border-none bg-grey-3'
                    inputProps={{ className: 'bg-grey-3' }}
                    placeholder={'Type'}
                    setQuery={setQuery}
                    query={query}
                  />
                  {canManageAccount && normalizedQuery && !labelExists && (
                    <div className='px-2 pt-1'>
                      <button
                        type='button'
                        className='w-full rounded px-4 py-2 text-left text-base hover:bg-brand hover:text-white'
                        onClick={() => handleCreate(query)}
                      >
                        {`+ Create "${normalizedQuery}"`}
                      </button>
                    </div>
                  )}
                </div>

                <div className='overflow-y-auto'>
                  <Combolist
                    items={filteredItems}
                    searchKeys={['name']}
                    placeholder={'Type'}
                    value={selectedItems}
                    onChange={handleCombolistChange}
                    multiple={true}
                    searchable={false}
                    renderInner={(item) => (
                      <div className='flex min-w-0 flex-col '>
                        <span className='truncate text-base'>{item.name}</span>
                      </div>
                    )}
                  />
                </div>

                <div className='z-10 flex items-center gap-2 border-t bg-white p-1.5 text-xs'>
                  <Popover.Button
                    className='flex-1 p-2 text-left uppercase'
                    onClick={() => setCancelling(true)}
                  >
                    Cancel
                  </Popover.Button>
                  <Popover.Button className='flex-1 p-2 text-right uppercase'>
                    OK
                  </Popover.Button>
                </div>
              </>
            )}
          </PopoverPanel>
        </MenuTransition>
      </Popover>
      {canManageAccount && (
        <CreateContactTypeDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          defaultValue={pendingCreateValue}
          onTypeCreated={(created) => {
            const exists = selectedValues.includes(created);
            const next = exists ? selectedValues : [...selectedValues, created];
            setValue(next);
            setPendingCreateValue('');
          }}
        />
      )}
    </div>
  );
}
